import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const SUPPORTED = ["tr", "en"] as const;
type Lang = (typeof SUPPORTED)[number];

const parseLang = (value?: string | null): Lang | null => {
  if (!value) return null;
  const code = value.trim().toLowerCase().split("-")[0];
  return (SUPPORTED as readonly string[]).includes(code) ? (code as Lang) : null;
};

/**
 * `/program/...?lng=tr|en` derin bağlantılarında URL'deki dili uygular.
 *
 * Fallback: `lng` yoksa veya desteklenmeyen bir değerse (örn. `?lng=de`,
 * `?lng=`), URL yok sayılır ve varsayılan dil kullanılır:
 * kayıtlı tercih (localStorage) → aktif i18n dili → `fallbackLng`.
 */
export const useUrlLanguage = () => {
  const { pathname, search } = useLocation();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const appliedFor = useRef<string | null>(null);

  useEffect(() => {
    if (!pathname.startsWith("/program")) return;

    const raw = new URLSearchParams(search).get("lng");
    const key = `${pathname}?${raw ?? ""}`;
    if (appliedFor.current === key) return;
    appliedFor.current = key;

    let stored: string | null = null;
    try {
      stored = localStorage.getItem("i18nextLng");
    } catch {
      /* storage unavailable */
    }

    const target =
      parseLang(raw) ??
      parseLang(stored) ??
      parseLang(i18n.language) ??
      parseLang(
        Array.isArray(i18n.options?.fallbackLng)
          ? i18n.options.fallbackLng[0]
          : (i18n.options?.fallbackLng as string | undefined),
      ) ??
      "en";

    if (parseLang(i18n.language) !== target) {
      void i18n.changeLanguage(target);
    }
  }, [pathname, search, i18n]);

  // Dil değişince URL'deki lng parametresini senkron tut (geçmişi kirletmeden).
  useEffect(() => {
    if (!pathname.startsWith("/program")) return;

    const sync = (next: string) => {
      const lang = parseLang(next);
      if (!lang) return;
      const params = new URLSearchParams(window.location.search);
      if (params.get("lng") === lang) return;
      params.set("lng", lang);
      appliedFor.current = `${pathname}?${lang}`;
      navigate(`${pathname}?${params.toString()}`, { replace: true });
    };

    i18n.on("languageChanged", sync);
    return () => i18n.off("languageChanged", sync);
  }, [pathname, i18n, navigate]);
};


export default useUrlLanguage;
